import {
  Injectable,
  signal,
  computed,
  WritableSignal,
  Signal,
} from '@angular/core';
import { DateTime, DateTimeUnit } from 'luxon';

export type DateFilterRange = 'Day' | 'Week' | 'Month' | 'Year' | 'Forecast';

export interface DateNavigationState {
  currentDate: WritableSignal<DateTime>;
  activeFilterRange: WritableSignal<DateFilterRange>;
  currentChartRange: Signal<{ start: DateTime; end: DateTime }>;

  rangePickerDates: Signal<DateTime[]>;
  daysInMonth: Signal<DateTime[]>;
  weeksInMonth: Signal<DateTime[]>;
  monthsInYear: Signal<DateTime[]>;
  yearsRange: Signal<DateTime[]>;
}

@Injectable({
  providedIn: 'root',
})
export class DateNavigationService {
  private readonly earliestSelectableDate = DateTime.fromISO('2024-01-01').startOf('day');

  createDateNavigation(
    initialDate: DateTime = DateTime.now(),
  ): DateNavigationState {
    const currentDate: WritableSignal<DateTime> = signal(initialDate);
    const activeFilterRange: WritableSignal<DateFilterRange> = signal('Day');

    const currentChartRange = computed(() => {
      const date = currentDate();
      const filterRange = activeFilterRange().toLowerCase() as DateTimeUnit;

      const startDate = date.startOf(filterRange);
      const endDate = date.endOf(filterRange);

      return { start: startDate, end: endDate };
    });

    const daysInMonth = computed(() => {
      const date = currentDate();
      const startOfMonth = date.startOf('month');
      const daysCount = date.daysInMonth ?? 31;

      return Array.from({ length: daysCount }, (_, i) =>
        startOfMonth.plus({ days: i }),
      );
    });

    const weeksInMonth = computed(() => {
      const date = currentDate();
      const startOfMonth = date.startOf('month');
      const endOfMonth = date.endOf('month');

      const weeks: DateTime[] = [];
      let currentWeek = startOfMonth.startOf('week');

      while (currentWeek <= endOfMonth) {
        weeks.push(currentWeek);
        currentWeek = currentWeek.plus({ weeks: 1 });
      }

      return weeks;
    });

    const monthsInYear = computed(() => {
      const date = currentDate();
      const startOfYear = date.startOf('year');

      return Array.from({ length: 12 }, (_, i) =>
        startOfYear.plus({ months: i }),
      );
    });

    const yearsRange = computed(() => {
      const date = currentDate();
      const currentYear = date.startOf('year');

      return Array.from({ length: 11 }, (_, i) =>
        currentYear.minus({ years: 5 - i }),
      ).filter(year => year >= this.earliestSelectableDate.startOf('year'));
    });

    const rangePickerDates = computed(() => {
      const range = activeFilterRange();

      switch (range) {
        case 'Day':
          return daysInMonth();
        case 'Week':
          return weeksInMonth();
        case 'Month':
          return monthsInYear();
        case 'Year':
          return yearsRange();
        case 'Forecast':
          return [];
      }
    });

    return {
      currentDate,
      activeFilterRange,
      currentChartRange,
      rangePickerDates,
      daysInMonth,
      weeksInMonth,
      monthsInYear,
      yearsRange,
    };
  }

  adjustCurrentDate(state: DateNavigationState, multiplier: number): void {
    const date = state.currentDate();
    const sign = Math.sign(multiplier);
    const isNegative = sign < 0;
    const absValue = Math.abs(multiplier);

    let modifier = {};
    switch (state.activeFilterRange()) {
      case 'Day':
        modifier = { days: absValue };
        break;
      case 'Week':
        modifier = { weeks: absValue };
        break;
      case 'Month':
        modifier = { months: absValue };
        break;
      case 'Year':
        modifier = { years: absValue };
        break;
      case 'Forecast':
        return;
    }

    const newDate = isNegative ? date.minus(modifier) : date.plus(modifier);

    if (!this.isDateDisabled(newDate)) {
      state.currentDate.set(newDate);
    }
  }

  isDateDisabled(date: DateTime): boolean {
    return false;
  }

  beginOfWeek(date: DateTime): DateTime {
    return date.startOf('week');
  }

  endOfWeek(date: DateTime): DateTime {
    return date.endOf('week');
  }

  formatDate(date: DateTime, range: DateFilterRange): string {
    let format = 'dd/MM/yyyy';
    switch (range) {
      case 'Day':
        format = 'dd/MM/yyyy';
        break;
      case 'Week':
        format = 'dd/MM';
        break;
      case 'Month':
        format = 'LLLL yyyy';
        break;
      case 'Year':
        format = 'yyyy';
        break;
      case 'Forecast':
        format = 'yyyy';
        break;
    }

    if (range === 'Week') {
      return `${this.beginOfWeek(date).toFormat(format)} - ${this.endOfWeek(date).toFormat(format)}`;
    }

    return date.toFormat(format);
  }

  formatPickerDate(date: DateTime, range: DateFilterRange): string {
    let format = 'dd';
    switch (range) {
      case 'Day':
        format = 'dd';
        break;
      case 'Week':
        format = 'dd/MM';
        break;
      case 'Month':
        format = 'LLLL';
        break;
      case 'Year':
        format = 'yyyy';
        break;
      case 'Forecast':
        format = 'yyyy';
        break;
    }

    if (range === 'Week') {
      return `${this.beginOfWeek(date).toFormat(format)} - ${this.endOfWeek(date).toFormat(format)}`;
    }

    return date.toFormat(format);
  }

  goBackward(state: DateNavigationState): void {
    this.adjustCurrentDate(state, -1);
  }

  goForward(state: DateNavigationState): void {
    this.adjustCurrentDate(state, 1);
  }

  updateCurrentDate(state: DateNavigationState, date: DateTime): boolean {
    if (!this.isDateDisabled(date)) {
      state.currentDate.set(date);
      return true;
    }
    return false;
  }
}
